/**
 * ElectronicModules — browseable list of the `electronicModules` entity
 * collection. Mirrors CuratedCalibres: a Firestore load (the entity is small;
 * Algolia is reserved for the reference-editor typeahead), client-side search,
 * a sortable table, and a row → editor-modal flow. Supports a deep-link
 * `openModuleId` so other surfaces can jump straight into one module's editor.
 *
 * Multi-select mirrors the calibre and winder listings: tick rows, act on them
 * from the floating ModuleBulkActionBar. One thing this page does NOT need that
 * those two do — hydration. Their rows come from Algolia projections, so a bulk
 * tool has to read the real documents back before it can write them; these rows
 * ARE the documents, so the selection can be handed straight to the bulk tools.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import {
  db,
  enrichElectronicModuleWithAI,
  getElectronicModules,
  getElectronicModuleById,
  getManufacturerCatalogue,
  type ManufacturerCatalogueEntry,
  type ModuleEnrichResult,
} from '../../services/firebase';
import type { ElectronicModule } from '../../types';
import { useManufacturerCatalogueChanged } from '../../hooks/useManufacturerCatalogue';
import {
  MODULE_ALWAYS_APPLY_KEYS,
  MODULE_ENRICH_FIELD_ORDER,
  MODULE_NEVER_DEFAULT_KEYS,
  buildModulePatch,
  coerceModuleValue,
  formatModuleValue,
  getModuleConfidence,
  getModuleExisting,
  getModuleFieldLabel,
  getModuleSuggestion,
  suggestedModuleKeys,
} from '../../services/moduleEnrichFields';
import {
  BulkEntityAIEnhance,
  type BulkEnhanceDescriptor,
} from '../common/BulkEntityAIEnhance';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { ElectronicModuleEditorModal } from './ElectronicModuleEditorModal';
import { ModuleBulkActionBar } from './ModuleBulkActionBar';
import toast from 'react-hot-toast';

const ELECTRONIC_MODULES_COLLECTION = 'electronicModules';

/** Which single-field bulk modal is open, if any. */
type FieldModal = 'manufacturer' | 'source' | 'imageSource';

interface ElectronicModulesProps {
  /** Deep-link: open this module's editor on mount. */
  openModuleId?: string;
  onOpenModuleConsumed?: () => void;
}

type SortKey = 'name' | 'manufacturer' | 'displayType' | 'referenceCount' | 'updatedAt';

function tsToMillis(v: unknown): number {
  if (!v) return 0;
  if (typeof v === 'object' && v !== null && 'toMillis' in v) {
    return (v as { toMillis: () => number }).toMillis();
  }
  if (v instanceof Date) return v.getTime();
  return 0;
}

function fmtDate(v: unknown): string {
  const ms = tsToMillis(v);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString();
}

export function ElectronicModules({
  openModuleId,
  onOpenModuleConsumed,
}: ElectronicModulesProps) {
  const [modules, setModules] = useState<ElectronicModule[]>([]);
  const [manufacturers, setManufacturers] = useState<ManufacturerCatalogueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ElectronicModule | null>(null);

  // Selection + bulk actions.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [aiEnhanceOpen, setAiEnhanceOpen] = useState(false);
  const [fieldModal, setFieldModal] = useState<FieldModal | null>(null);
  const [fieldValue, setFieldValue] = useState('');

  const manufacturerName = useCallback(
    (id?: string) => {
      if (!id) return '';
      const m = manufacturers.find((x) => x.id === id);
      return m ? m.displayName || m.name : id;
    },
    [manufacturers],
  );

  const load = useCallback(async (fromServer = false) => {
    setLoading(true);
    try {
      // The modules themselves are a Firestore read (the collection is small and
      // the row → editor flow needs whole documents); the manufacturers are only
      // ever resolved to a display name, so they come from Algolia.
      const [mods, mans] = await Promise.all([
        getElectronicModules(fromServer),
        getManufacturerCatalogue(),
      ]);
      setModules(mods);
      setManufacturers(mans);
    } catch (err) {
      console.error('Failed to load electronic modules:', err);
      toast.error('Failed to load electronic modules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // A manufacturer created or deleted elsewhere (the module editor's
  // quick-create, most often) changes what `manufacturerLabel` above can
  // resolve. Reload rather than render the new one as a raw slug until the tab
  // is remounted — the overlay makes this reload correct straight away even
  // though Algolia is a second or two behind. See
  // `services/manufacturerCatalogueOverlay.ts`.
  useManufacturerCatalogueChanged(() => void load());

  // Deep-link: open one module's editor straight away.
  useEffect(() => {
    if (!openModuleId) return;
    let cancelled = false;
    getElectronicModuleById(openModuleId)
      .then((m) => {
        if (cancelled) return;
        if (m) {
          setEditing(m);
          setEditorOpen(true);
        } else {
          toast.error('Module not found');
        }
      })
      .catch(() => toast.error('Failed to open module'))
      .finally(() => onOpenModuleConsumed?.());
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModuleId]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc((v) => !v);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q
      ? modules.filter((m) => {
          const hay = [
            m.name,
            manufacturerName(m.manufacturerId),
            m.displayType,
            m.battery,
            ...(m.alsoKnownAs || []),
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return hay.includes(q);
        })
      : [...modules];

    rows.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = (a.name || '').localeCompare(b.name || '');
          break;
        case 'manufacturer':
          cmp = manufacturerName(a.manufacturerId).localeCompare(
            manufacturerName(b.manufacturerId),
          );
          break;
        case 'displayType':
          cmp = (a.displayType || '').localeCompare(b.displayType || '');
          break;
        case 'referenceCount':
          cmp = (a.referenceCount || 0) - (b.referenceCount || 0);
          break;
        case 'updatedAt':
          cmp = tsToMillis(a.updatedAt) - tsToMillis(b.updatedAt);
          break;
      }
      return sortAsc ? cmp : -cmp;
    });
    return rows;
  }, [modules, search, sortKey, sortAsc, manufacturerName]);

  const selectedModules = useMemo(
    () => modules.filter((m) => selectedIds.has(m.id)),
    [modules, selectedIds],
  );

  // Selection is keyed by id, so a row that drops out of the filtered view
  // stays selected. Clearing on a search change would silently discard work the
  // admin had already ticked.
  const toggleRow = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allVisibleSelected =
    filtered.length > 0 && filtered.every((m) => selectedIds.has(m.id));

  const toggleAllVisible = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) for (const m of filtered) next.delete(m.id);
      else for (const m of filtered) next.add(m.id);
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const openFieldModal = (modal: FieldModal) => {
    setFieldValue('');
    setFieldModal(modal);
  };

  // ---- Bulk writes ------------------------------------------------------------

  /**
   * Set one field across the selection.
   *
   * FILL-EMPTY by design, matching every other bulk admin action: a module that
   * already has a value for the field is skipped, never overwritten. There is no
   * force-overwrite toggle — destructive single-field edits go through the
   * editor, one module at a time.
   */
  const applyFieldToSelection = async (
    field: 'manufacturerId' | 'source',
    value: string,
  ) => {
    const trimmed = value.trim();
    if (!trimmed) {
      toast.error('Pick a value first');
      return;
    }
    setBulkBusy(true);
    let written = 0;
    let skipped = 0;
    let failed = 0;
    try {
      for (const m of selectedModules) {
        const existing = (m as unknown as Record<string, unknown>)[field];
        if (typeof existing === 'string' && existing.trim()) {
          skipped += 1;
          continue;
        }
        try {
          await updateDoc(doc(db, ELECTRONIC_MODULES_COLLECTION, m.id), {
            [field]: trimmed,
            updatedAt: serverTimestamp(),
          });
          written += 1;
        } catch (err) {
          console.error(`Bulk ${field} failed for ${m.id}:`, err);
          failed += 1;
        }
      }
      const parts = [`${written} updated`];
      if (skipped) parts.push(`${skipped} already set`);
      if (failed) parts.push(`${failed} failed`);
      if (failed) toast.error(parts.join(', '));
      else toast.success(parts.join(', '));
      await load(true);
    } finally {
      setBulkBusy(false);
      setFieldModal(null);
      setFieldValue('');
    }
  };

  /**
   * Set the attribution `source` on every image of every selected module.
   *
   * The one bulk action here that DOES overwrite, exactly as the calibre
   * listing's does: `source` is required on every image, so "fill the empty
   * ones" would be a no-op. Hence the count in the confirm — an admin should
   * see how many images they are about to re-attribute before saying yes.
   */
  const applyImageSourceToSelection = async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      toast.error('Enter a source first');
      return;
    }
    const withImages = selectedModules.filter((m) => (m.images?.length ?? 0) > 0);
    const totalImages = withImages.reduce((n, m) => n + (m.images?.length ?? 0), 0);
    if (withImages.length === 0) {
      toast.error('None of the selected modules have images to update');
      return;
    }
    const ok = confirm(
      `Set image source to "${trimmed}" on ${withImages.length} module${withImages.length === 1 ? '' : 's'}? ` +
        `This will overwrite the current source value on ${totalImages} image${totalImages === 1 ? '' : 's'}.`,
    );
    if (!ok) return;

    setBulkBusy(true);
    let written = 0;
    let failed = 0;
    try {
      for (const m of withImages) {
        try {
          await updateDoc(doc(db, ELECTRONIC_MODULES_COLLECTION, m.id), {
            images: (m.images ?? []).map((img) => ({ ...img, source: trimmed })),
            updatedAt: serverTimestamp(),
          });
          written += 1;
        } catch (err) {
          console.error(`Bulk image source failed for ${m.id}:`, err);
          failed += 1;
        }
      }
      if (failed) toast.error(`${written} updated, ${failed} failed`);
      else
        toast.success(
          `Updated source on ${written} module${written === 1 ? '' : 's'} / ${totalImages} image${totalImages === 1 ? '' : 's'}`,
        );
      await load(true);
    } finally {
      setBulkBusy(false);
      setFieldModal(null);
      setFieldValue('');
    }
  };

  /**
   * The module binding of the shared bulk AI dialog.
   *
   * `enrichElectronicModule` reads the persisted document server-side, so the
   * runner passes an id and nothing else — every selected row has one by
   * definition. Per-field confidence, like winders and unlike calibres.
   */
  const aiDescriptor = useMemo<BulkEnhanceDescriptor<ElectronicModule, ModuleEnrichResult>>(
    () => ({
      collection: ELECTRONIC_MODULES_COLLECTION,
      formName: 'moduleEnrich',
      enrich: (entry) => enrichElectronicModuleWithAI(entry.id),

      fieldOrder: MODULE_ENRICH_FIELD_ORDER,
      suggestedKeys: suggestedModuleKeys,
      fieldLabel: getModuleFieldLabel,
      getExisting: getModuleExisting,
      getSuggestion: getModuleSuggestion,
      getConfidence: getModuleConfidence,
      coerceValue: coerceModuleValue,
      formatValue: formatModuleValue,
      buildPatch: buildModulePatch,
      alwaysApplyKeys: MODULE_ALWAYS_APPLY_KEYS,
      neverDefaultKeys: MODULE_NEVER_DEFAULT_KEYS,

      rowLabel: (entry) => entry.name,
      rowSubLabel: (entry) => manufacturerName(entry.manufacturerId),
      copy: {
        title: (count) => `AI Module Enhancement — ${count} selected`,
        rowHeader: 'Module',
        runButton: (count) => `Run AI on ${count} module${count === 1 ? '' : 's'}`,
        runComplete: (ok) => `Enhanced ${ok} module${ok === 1 ? '' : 's'} — review below`,
        applied: (fields, rows) =>
          `Applied ${fields} field${fields === 1 ? '' : 's'} across ${rows} module${rows === 1 ? '' : 's'}`,
        appliedWithFailures: (rows, failed) =>
          `Applied ${rows} module${rows === 1 ? '' : 's'}, ${failed} failed`,
        failures: (count) => `${count} module(s) failed to enhance`,
        configNote: (
          <>
            The threshold + mode decide which cells start <em>checked</em> in the
            review grid. Every proposed value is shown regardless — you can always
            check a cell manually. In “fill empty” mode, fields that already have a
            value are highlighted and start unchecked.{' '}
            <strong>
              AI description starts checked whenever it meets the confidence
              threshold
            </strong>{' '}
            (independent of the fill/overwrite mode), and{' '}
            <strong>Name never starts checked</strong> — a rename is shown as a
            column only when the model proposes a genuinely different name, and is
            yours to tick row by row. The manufacturer is not written by this tool:
            the model answers with a maker's name, and the document needs an id.
          </>
        ),
        legendNote: (
          <>
            Confidence is <em>per field</em> for modules — the dot beside each value
            is what the threshold reads; the dot beside the module name is the best
            confidence in that row. A cell that agrees with what is already stored
            writes nothing and is skipped on apply.
          </>
        ),
      },
    }),
    [manufacturerName],
  );

  const openNew = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (m: ElectronicModule) => {
    setEditing(m);
    setEditorOpen(true);
  };

  const sortIndicator = (key: SortKey) =>
    sortKey === key ? (sortAsc ? ' ▲' : ' ▼') : '';

  const headerClass = 'text-left font-medium px-3 py-2 cursor-pointer select-none hover:text-gray-900';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-gray-900">
          Electronic Modules
        </h2>
        <span className="text-sm text-gray-500">{modules.length} total</span>
        <div className="ml-auto flex items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search modules…"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-64"
          />
          <Button variant="secondary" size="sm" onClick={() => load(true)}>
            Refresh
          </Button>
          <Button variant="primary" size="sm" onClick={openNew}>
            + New module
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-500 italic py-8 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-gray-500 italic py-8 text-center border border-dashed border-gray-200 rounded-lg">
          {modules.length === 0
            ? 'No electronic modules yet. Create one to get started.'
            : 'No modules match your search.'}
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 border-b border-gray-200">
              <tr>
                <th className="w-10 px-3 py-2">
                  <input
                    type="checkbox"
                    aria-label="Select all visible modules"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                  />
                </th>
                <th className={headerClass} onClick={() => toggleSort('name')}>
                  Name{sortIndicator('name')}
                </th>
                <th
                  className={headerClass}
                  onClick={() => toggleSort('manufacturer')}
                >
                  Manufacturer{sortIndicator('manufacturer')}
                </th>
                <th
                  className={headerClass}
                  onClick={() => toggleSort('displayType')}
                >
                  Display{sortIndicator('displayType')}
                </th>
                <th className="text-left font-medium px-3 py-2">Battery</th>
                <th
                  className={headerClass}
                  onClick={() => toggleSort('referenceCount')}
                >
                  Refs{sortIndicator('referenceCount')}
                </th>
                <th
                  className={headerClass}
                  onClick={() => toggleSort('updatedAt')}
                >
                  Updated{sortIndicator('updatedAt')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((m) => (
                <tr
                  key={m.id}
                  onClick={() => openEdit(m)}
                  className={`cursor-pointer hover:bg-gray-50 ${
                    selectedIds.has(m.id) ? 'bg-brand-50' : ''
                  }`}
                >
                  {/* Ticking a row must not also open its editor. */}
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${m.name}`}
                      checked={selectedIds.has(m.id)}
                      onChange={() => toggleRow(m.id)}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-800">
                    {m.name}
                    {m.alsoKnownAs && m.alsoKnownAs.length > 0 && (
                      <span className="ml-2 text-xs text-gray-400">
                        ({m.alsoKnownAs.slice(0, 2).join(', ')}
                        {m.alsoKnownAs.length > 2 ? '…' : ''})
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {manufacturerName(m.manufacturerId) || (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {m.displayType || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {m.battery || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {m.referenceCount ?? 0}
                  </td>
                  <td className="px-3 py-2 text-gray-500">
                    {fmtDate(m.updatedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedIds.size > 0 && (
        <ModuleBulkActionBar
          count={selectedIds.size}
          busy={bulkBusy}
          onAiEnhance={() => setAiEnhanceOpen(true)}
          onSetManufacturer={() => openFieldModal('manufacturer')}
          onSetSource={() => openFieldModal('source')}
          onSetImageSource={() => openFieldModal('imageSource')}
          onClearSelection={clearSelection}
        />
      )}

      <BulkEntityAIEnhance
        isOpen={aiEnhanceOpen}
        onClose={() => setAiEnhanceOpen(false)}
        entries={selectedModules}
        onApplied={() => {
          void load(true);
          clearSelection();
        }}
        descriptor={aiDescriptor}
      />

      <Modal
        isOpen={fieldModal !== null}
        onClose={() => {
          if (!bulkBusy) setFieldModal(null);
        }}
        title={
          fieldModal === 'manufacturer'
            ? `Set manufacturer — ${selectedIds.size} selected`
            : fieldModal === 'source'
              ? `Set source — ${selectedIds.size} selected`
              : `Set image source — ${selectedIds.size} selected`
        }
        size="md"
      >
        <div className="space-y-4">
          {fieldModal === 'imageSource' ? (
            <p className="text-sm text-gray-600">
              Applies to <strong>every image</strong> of every selected module, and{' '}
              <strong>overwrites</strong> the source each one already carries —
              every image has one, so there are no empties to fill. Modules with no
              images are left alone.
            </p>
          ) : (
            <p className="text-sm text-gray-600">
              Applies to every selected module that does <strong>not</strong>{' '}
              already have a value. Existing values are never overwritten.
            </p>
          )}

          {fieldModal === 'manufacturer' ? (
            <select
              value={fieldValue}
              aria-label="Manufacturer"
              disabled={bulkBusy}
              onChange={(e) => setFieldValue(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">Select a manufacturer…</option>
              {[...manufacturers]
                .sort((a, b) =>
                  (a.displayName || a.name).localeCompare(b.displayName || b.name),
                )
                .map((man) => (
                  <option key={man.id} value={man.id}>
                    {man.displayName || man.name}
                  </option>
                ))}
            </select>
          ) : (
            <input
              type="text"
              value={fieldValue}
              aria-label={fieldModal === 'source' ? 'Source' : 'Image source'}
              disabled={bulkBusy}
              onChange={(e) => setFieldValue(e.target.value)}
              placeholder="e.g. casio-module-list, manual"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          )}

          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setFieldModal(null)}
              disabled={bulkBusy}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={bulkBusy}
              disabled={bulkBusy || !fieldValue.trim()}
              onClick={() => {
                if (fieldModal === 'imageSource') {
                  void applyImageSourceToSelection(fieldValue);
                } else if (fieldModal) {
                  void applyFieldToSelection(
                    fieldModal === 'manufacturer' ? 'manufacturerId' : 'source',
                    fieldValue,
                  );
                }
              }}
            >
              Apply to {selectedIds.size}
            </Button>
          </div>
        </div>
      </Modal>

      <ElectronicModuleEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        module={editing}
        onSaved={() => {
          // Keep the editor open (the modal self-manages closing) so a
          // just-created module can be AI-enriched in place; just refresh.
          void load(true);
        }}
        onDeleted={() => {
          setEditorOpen(false);
          void load(true);
        }}
      />
    </div>
  );
}
